import { useEffect, useState } from "react";
import {
  listCustomerRequests,
  updateCustomerRequest,
  deleteCustomerRequest,
} from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Inbox,
  Mail,
  Phone,
  Building2,
  Users2,
  Wrench,
  MessageSquare,
  Trash2,
  Check,
  X,
  Loader2,
  Calendar,
} from "lucide-react";

const STATUS_META = {
  new: { label: "New", className: "bg-amber-100 text-amber-800 border-amber-200" },
  contacted: { label: "Contacted", className: "bg-blue-100 text-blue-800 border-blue-200" },
  converted: { label: "Converted", className: "bg-kale-100 text-kale-800 border-kale-200" },
  dismissed: { label: "Dismissed", className: "bg-stone-100 text-stone-700 border-stone-200" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CustomerRequestsSection() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await listCustomerRequests();
      setRequests(res.data || []);
    } catch (err) {
      toast.error("Could not load sign-up requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const newCount = requests.filter((r) => r.status === "new").length;

  const openRequest = (r) => {
    setSelected(r);
    setNotesDraft(r.admin_notes || "");
    // Auto-mark as contacted when opened, if currently "new"
    if (r.status === "new") {
      updateCustomerRequest(r.id, { status: "contacted" })
        .then((res) => setRequests((rs) => rs.map((x) => (x.id === r.id ? res.data : x))))
        .catch(() => {});
    }
  };

  const changeStatus = async (id, status) => {
    setBusy(true);
    try {
      const res = await updateCustomerRequest(id, { status });
      setRequests((rs) => rs.map((x) => (x.id === id ? res.data : x)));
      setSelected(res.data);
      toast.success(`Marked as ${STATUS_META[status].label.toLowerCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await updateCustomerRequest(selected.id, { admin_notes: notesDraft });
      setRequests((rs) => rs.map((x) => (x.id === selected.id ? res.data : x)));
      setSelected(res.data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Could not save notes");
    } finally {
      setBusy(false);
    }
  };

  const removeRequest = async (id) => {
    if (!window.confirm("Permanently delete this request?")) return;
    setBusy(true);
    try {
      await deleteCustomerRequest(id);
      setRequests((rs) => rs.filter((x) => x.id !== id));
      setSelected(null);
      toast.success("Request deleted");
    } catch (err) {
      toast.error("Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="customer-requests-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Inbox className="w-4 h-4 text-kale-600" />
          New Customer Requests
          {newCount > 0 && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white ml-1" data-testid="new-requests-count">
              {newCount} new
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Sign-ups from the marketing landing page. Opening a request marks it as contacted.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 flex items-center justify-center text-stone-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading requests…
          </div>
        ) : requests.length === 0 ? (
          <div className="p-6 text-center text-sm text-stone-500">
            No sign-up requests yet. Share your landing page to start collecting them.
          </div>
        ) : (
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {requests.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.new;
              return (
                <button
                  key={r.id}
                  onClick={() => openRequest(r)}
                  className="w-full text-left px-4 py-3.5 hover:bg-stone-50 transition-colors flex items-start gap-3"
                  data-testid={`request-row-${r.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold shrink-0 ${
                      r.status === "new" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"
                    }`}
                  >
                    {r.contact_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-stone-900 truncate">{r.restaurant_name}</p>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.className}`}>
                        {meta.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-600 truncate">
                      {r.contact_name} · {r.email}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {formatDate(r.created_at)}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="request-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-kale-700" />
                  {selected.restaurant_name}
                </DialogTitle>
                <DialogDescription>
                  Submitted {formatDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-2">
                <Row icon={Users2} label="Contact" value={selected.contact_name} />
                <Row
                  icon={Mail}
                  label="Email"
                  value={
                    <a href={`mailto:${selected.email}`} className="text-kale-800 hover:underline break-all">
                      {selected.email}
                    </a>
                  }
                />
                <Row
                  icon={Phone}
                  label="Phone"
                  value={
                    <a href={`tel:${selected.phone}`} className="text-kale-800 hover:underline">
                      {selected.phone}
                    </a>
                  }
                />
                {selected.location_count && (
                  <Row icon={Building2} label="Locations" value={selected.location_count} />
                )}
                {selected.current_tool && (
                  <Row icon={Wrench} label="Currently using" value={selected.current_tool} />
                )}
                {selected.message && (
                  <Row
                    icon={MessageSquare}
                    label="Message"
                    value={<span className="whitespace-pre-wrap">{selected.message}</span>}
                  />
                )}

                <div className="pt-2">
                  <label className="text-xs font-medium text-stone-500 mb-1 block">Admin notes</label>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Add internal notes (e.g. called Aug 20, follow up next week)…"
                    className="min-h-[80px] text-sm"
                    data-testid="request-notes"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" variant="outline" onClick={saveNotes} disabled={busy} data-testid="save-notes">
                      Save notes
                    </Button>
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || selected.status === "converted"}
                    onClick={() => changeStatus(selected.id, "converted")}
                    className="border-kale-300 text-kale-800 hover:bg-kale-50"
                    data-testid="mark-converted"
                  >
                    <Check className="w-4 h-4 mr-1" /> Mark converted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || selected.status === "contacted"}
                    onClick={() => changeStatus(selected.id, "contacted")}
                    data-testid="mark-contacted"
                  >
                    Mark contacted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || selected.status === "dismissed"}
                    onClick={() => changeStatus(selected.id, "dismissed")}
                    data-testid="mark-dismissed"
                  >
                    <X className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                </div>
              </div>

              <DialogFooter className="flex-row gap-2">
                <Button
                  variant="ghost"
                  onClick={() => removeRequest(selected.id)}
                  disabled={busy}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-request"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button className="flex-1 btn-primary" onClick={() => setSelected(null)} data-testid="close-detail">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-stone-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-stone-500">{label}</p>
        <p className="text-sm text-stone-800 break-words">{value}</p>
      </div>
    </div>
  );
}
